import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCLP, dateLabel, statusBadge, ACCREDITATION_META } from "../../lib/format";
import { MapPin, CircleNotch, CheckCircle, ArrowRight, TrendUp, Star, Package, Warning, Truck, NavigationArrow } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { REGION_NAMES } from "../../lib/chile";

export default function CarrierDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [market, setMarket] = useState(null);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("market");
  const [counterFor, setCounterFor] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMsg, setCounterMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(false);
  const [config, setConfig] = useState({});
  const [vehicleId, setVehicleId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  const watchId = useRef(null);

  const loadProfile = () => api.get("/carrier/profile").then((r) => {
    setProfile(r.data);
    if (r.data.vehicles?.length && !vehicleId) setVehicleId(r.data.vehicles[0].id);
    if (r.data.drivers?.length && !driverId) setDriverId(r.data.drivers[0].id);
  }).catch(() => {});
  const loadMarket = () => {
    const params = {};
    if (fRegion) params.region = fRegion;
    if (fVehicle) params.vehicleType = fVehicle;
    return api.get("/marketplace", { params }).then((r) => setMarket(r.data)).catch(() => setMarket([]));
  };
  const loadServices = () => api.get("/services/mine").then((r) => setServices(r.data)).catch(() => {});

  useEffect(() => { loadProfile(); loadServices(); api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (profile?.accreditationStatus === "APROBADO") loadMarket(); }, [profile, fRegion, fVehicle]);
  useEffect(() => () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); }, []);

  const approved = profile?.accreditationStatus === "APROBADO";

  // Compartir ubicación en vivo (estilo Uber) usando el GPS del dispositivo.
  const toggleOnline = (val) => {
    if (val) {
      if (!navigator.geolocation) { toast.error("Tu dispositivo no soporta geolocalización"); return; }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          setOnline(true);
          const { latitude, longitude } = pos.coords;
          await api.post("/carrier/location", { lat: latitude, lng: longitude }).catch(() => {});
          toast.success("Estás en línea. Compartiendo tu ubicación.");
          watchId.current = navigator.geolocation.watchPosition(
            (p) => api.post("/carrier/location", { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {}),
            () => {}, { enableHighAccuracy: true, maximumAge: 15000 }
          );
        },
        () => toast.error("Debes permitir el acceso a tu ubicación"),
        { enableHighAccuracy: true }
      );
    } else {
      if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
      setOnline(false);
      api.post("/carrier/offline").catch(() => {});
      toast("Estás desconectado", { icon: "🔌" });
    }
  };

  const accept = async (f) => {
    if (!vehicleId) return toast.error("Selecciona el vehículo con el que realizarás el flete");
    if (!driverId) return toast.error("Designa el conductor que realizará el flete");
    setBusy(true);
    try {
      await api.post(`/freights/${f.id}/offer`, { type: "ACCEPT", offeredPrice: f.proposedPrice, vehicleId, driverId });
      toast.success("¡Precio aceptado! El cliente fue notificado.");
      loadMarket();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const sendCounter = async () => {
    if (!(Number(counterPrice) > 0)) return;
    if (!vehicleId) return toast.error("Selecciona un vehículo");
    if (!driverId) return toast.error("Designa un conductor");
    setBusy(true);
    try {
      await api.post(`/freights/${counterFor.id}/offer`, { type: "COUNTEROFFER", offeredPrice: Number(counterPrice), message: counterMsg, vehicleId, driverId });
      toast.success("Contraoferta enviada");
      setCounterFor(null); setCounterPrice(""); setCounterMsg("");
      loadMarket();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const completed = services.filter((s) => s.status === "COMPLETADO");
  const income = completed.reduce((a, s) => a + (s.agreedPrice || 0), 0);
  const activeServices = services.filter((s) => !["COMPLETADO", "CANCELADO"].includes(s.status));

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8" data-testid="carrier-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Panel del transportista</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Hola, {user.name}</h1>
        </div>
        {approved && (
          <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${online ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white"}`} data-testid="online-toggle">
            <NavigationArrow size={22} weight="fill" className={online ? "text-emerald-600" : "text-zinc-400"} />
            <div className="mr-1">
              <div className="text-sm font-semibold leading-tight">{online ? "En línea" : "Desconectado"}</div>
              <div className="text-xs text-zinc-500">Compartir mi ubicación</div>
            </div>
            <Switch checked={online} onCheckedChange={toggleOnline} data-testid="online-switch" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Metric icon={CheckCircle} label="Completados" value={completed.length} />
        <Metric icon={Star} label="Calificación" value={profile?.rating ? profile.rating.toFixed(1) : "—"} />
        <Metric icon={TrendUp} label="Ingresos" value={formatCLP(income)} />
        <Metric icon={Package} label="En curso" value={activeServices.length} />
      </div>

      {profile && !approved && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-6 flex items-start gap-3" data-testid="accreditation-warning">
          <Warning size={24} weight="fill" className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-800">Acreditación {ACCREDITATION_META[profile.accreditationStatus]?.label.toLowerCase()}</div>
            <p className="text-sm text-amber-700">Debes estar APROBADO para tomar servicios. Completa y envía tu acreditación.</p>
          </div>
          <Link to="/app/carrier/onboarding" className="px-4 py-2 rounded-full bg-amber-600 text-white text-sm font-semibold whitespace-nowrap hover:bg-amber-700 transition-colors">
            Ir a acreditación
          </Link>
        </div>
      )}

      <div className="mb-5" data-testid="market-heading">
        <h2 className="text-lg font-bold">Fletes disponibles</h2>
      </div>

      {(
        approved ? (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-5" data-testid="market-toolbar">
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Vehículo para ofertar</label>
                <select data-testid="offer-vehicle-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  {(profile?.vehicles || []).length === 0 && <option value="">Agrega un vehículo en tu acreditación</option>}
                  {(profile?.vehicles || []).map((v) => (
                    <option key={v.id} value={v.id}>{v.type}{v.licensePlate ? ` · ${v.licensePlate}` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Conductor designado</label>
                <select data-testid="offer-driver-select" value={driverId} onChange={(e) => setDriverId(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  {(profile?.drivers || []).length === 0 && <option value="">Agrega un conductor</option>}
                  {(profile?.drivers || []).map((d) => (
                    <option key={d.id} value={d.id}>{d.name} {d.lastName}{d.licenseClass ? ` · ${d.licenseClass}` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Filtrar por región</label>
                <select data-testid="filter-region" value={fRegion} onChange={(e) => setFRegion(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  <option value="">Todas las regiones</option>
                  {REGION_NAMES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Filtrar por vehículo sugerido</label>
                <select data-testid="filter-vehicle" value={fVehicle} onChange={(e) => setFVehicle(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  <option value="">Todos los tipos</option>
                  {(config.vehicleTypes || []).map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            {market === null ? (
              <div className="flex justify-center py-16"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>
            ) : market.length === 0 ? (
              <Empty text="No hay fletes disponibles con estos filtros. Ajusta la región o el tipo de vehículo." />
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="marketplace-list">
              {market.map((f) => (
                <div key={f.id} data-testid={`market-card-${f.id}`} className="rounded-2xl border border-zinc-200 bg-white p-5 hover:-translate-y-1 transition-transform">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A36]">{f.category}</span>
                    <div className="flex items-center gap-2">
                      {f.matchScore >= 40 && (
                        <span data-testid={`match-badge-${f.id}`}
                          className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${f.matchScore >= 65 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {f.matchScore >= 65 ? "Alto match" : "Buen match"} · {f.matchScore}%
                        </span>
                      )}
                      {f.distanceKm != null && <span className="text-xs text-zinc-500">~{f.distanceKm} km</span>}
                    </div>
                  </div>
                  <div className="font-semibold mt-1.5 flex items-center gap-1.5"><MapPin size={16} className="text-zinc-400" />{f.origin.city} → {f.destination.city}</div>
                  <div className="text-xs text-zinc-500 mt-1">{f.asap ? "Lo antes posible" : `${dateLabel(f.requestedDate)} · ${f.timeRange}`}</div>
                  {f.client && (
                    <div className="text-xs text-zinc-600 mt-1.5 flex items-center gap-1.5" data-testid={`client-rep-${f.id}`}>
                      <span className="text-zinc-400">Cliente:</span>
                      <span className="font-medium">{f.client.name}</span>
                      {f.client.ratingCount > 0
                        ? <span className="flex items-center gap-0.5 text-zinc-500"><Star size={11} weight="fill" className="text-[#FF5A36]" />{f.client.rating} ({f.client.ratingCount})</span>
                        : <span className="text-zinc-400">Sin calificaciones</span>}
                    </div>
                  )}
                  {f.matchReasons?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid={`match-reasons-${f.id}`}>
                      {f.matchReasons.map((r) => <span key={r} className="text-[10px] font-semibold bg-[#FFF4F1] text-[#FF5A36] rounded-full px-2 py-0.5 flex items-center gap-1"><CheckCircle size={10} weight="fill" />{r}</span>)}
                    </div>
                  )}
                  {f.recommendedVehicles?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {f.recommendedVehicles.map((v) => <span key={v} className="text-[10px] font-semibold bg-zinc-100 text-zinc-600 rounded-full px-2 py-0.5 flex items-center gap-1"><Truck size={10} />{v}</span>)}
                    </div>
                  )}
                  <div className="mt-3">
                    <span className="text-xs text-zinc-500">Precio ofrecido</span>
                    <div className="font-display text-3xl font-black">{formatCLP(f.proposedPrice)}</div>
                  </div>
                  {f.myOffer ? (
                    <div className="mt-4 text-center text-sm font-semibold text-emerald-600 bg-emerald-50 rounded-xl py-2.5">Ya enviaste una oferta</div>
                  ) : (
                    <div className="mt-4 flex flex-col gap-2">
                      <button onClick={() => accept(f)} disabled={busy} data-testid={`accept-${f.id}`}
                        className="py-3 rounded-xl bg-[#FF5A36] text-white font-bold hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
                        ACEPTAR {formatCLP(f.proposedPrice)}
                      </button>
                      <button onClick={() => { setCounterFor(f); setCounterPrice(String(f.proposedPrice)); }} data-testid={`counter-${f.id}`}
                        className="py-3 rounded-xl border border-zinc-300 font-semibold hover:border-[#0F1115] transition-colors active:scale-95">
                        CONTRAOFERTAR
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </>
        ) : (
          <Empty text="Acredítate para ver los fletes disponibles." />
        )
      )}

      <Dialog open={!!counterFor} onOpenChange={(o) => !o && setCounterFor(null)}>
        <DialogContent data-testid="counter-dialog">
          <DialogHeader><DialogTitle>Contraoferta</DialogTitle></DialogHeader>
          {counterFor && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm bg-zinc-100 rounded-xl px-4 py-3">
                <span className="text-zinc-500">Precio cliente</span>
                <span className="font-bold">{formatCLP(counterFor.proposedPrice)}</span>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Mi propuesta</label>
                <div className="relative mt-1.5">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-black text-zinc-400">$</span>
                  <input data-testid="counter-price" type="number" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)}
                    className="w-full h-14 pl-9 pr-4 rounded-xl border-2 border-zinc-300 font-display text-2xl font-black focus:outline-none focus:border-[#FF5A36]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Mensaje (opcional)</label>
                <textarea data-testid="counter-message" rows={2} value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)}
                  placeholder="Por distancia y volumen puedo realizarlo por..."
                  className="mt-1.5 w-full px-4 py-3 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36] resize-none" />
              </div>
              <button onClick={sendCounter} disabled={busy} data-testid="send-counter"
                className="w-full py-3.5 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
                {busy ? <CircleNotch size={18} className="animate-spin" /> : "Enviar contraoferta"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <Icon size={20} className="text-[#FF5A36] mb-2" weight="bold" />
      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="font-display text-xl font-black truncate">{value}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? "border-[#FF5A36] text-[#FF5A36]" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
      {children}
    </button>
  );
}
function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="empty-state">{text}</div>;
}
